import type { ChatMessage, ChatSliceState, UploadItem } from "../types/redux";

export type UploadUpdatePayload = Partial<UploadItem> & { uploadId: string };

type UploadMap = Record<string, UploadItem>;

/** Backend marks a file "Ready" once ingestion has finished. */
const READY_STATUS = "Ready";
const MAX_ERROR_LENGTH = 200;

const readyPrompt = (successCount: number): string =>
  successCount === 1
    ? "Your file is ready. Do you have more files to upload, or can I proceed to read it, extract relevant context, and create an outline for you to review?"
    : `All ${successCount} files are ready. Do you have more files to upload, or can I proceed to read them, extract relevant context, and create an outline for you to review?`;

/** The upload table still accepting entries, created on first use. */
function openUploadTable(state: ChatSliceState): ChatMessage {
  const existing = state.messages.find(
    (msg: ChatMessage) => msg.type === "upload-table" && !msg.allCompleted
  );
  if (existing) return existing;

  const created: ChatMessage = {
    sender: "chatbot",
    type: "upload-table",
    clientMessageId: `upload-table-${Date.now()}`,
    text: "",
    uploads: {},
    allCompleted: false,
  };
  state.messages.push(created);
  return created;
}

/** An omitted field means "leave it alone", so fall back to what's stored. */
function pick<T>(incoming: T | undefined, stored: T | undefined): T | undefined {
  return incoming !== undefined ? incoming : stored;
}

/** Merge one status update into the table, keeping fields the caller omitted. */
function mergeUpload(table: ChatMessage, payload: UploadUpdatePayload): void {
  const { uploadId, fileName, fileSize, progress, status, completed, error, docId } = payload;
  const existing: Partial<UploadItem> = (table.uploads as UploadMap)?.[uploadId] || {};

  if (!table.uploads) {
    table.uploads = {};
  }

  (table.uploads as UploadMap)[uploadId] = {
    id: uploadId,
    fileName: pick(fileName, existing.fileName),
    fileSize: pick(fileSize, existing.fileSize),
    progress: pick(progress, existing.progress) || 0,
    status: pick(status, existing.status),
    completed: pick(completed, existing.completed) || false,
    error: pick(error, existing.error) || false,
    docId: pick(docId, existing.docId),
  };
}

const succeeded = (uploads: UploadItem[]): UploadItem[] =>
  uploads.filter((upload) => upload.completed && !upload.error);

/** True once every file that uploaded cleanly has also finished processing. */
const allProcessed = (uploads: UploadItem[]): boolean =>
  succeeded(uploads).every((upload) => upload.status === READY_STATUS);

/**
 * A lone failure carries its reason in `status`; surface it (trimmed) so the
 * user sees why rather than a bare count.
 */
function failureSummary(uploads: UploadItem[], failCount: number): string {
  if (failCount !== 1) {
    return `${failCount} files failed to upload.`;
  }

  const status = uploads[0]?.status;
  const rawErrorMsg = status && status !== "error" ? status.replace(/[\r\n]+/g, " ").trim() : null;
  if (!rawErrorMsg) {
    return "1 file failed to upload.";
  }

  const message =
    rawErrorMsg.length > MAX_ERROR_LENGTH
      ? rawErrorMsg.slice(0, MAX_ERROR_LENGTH) + "…"
      : rawErrorMsg;
  return `1 file failed to upload: ${message}`;
}

function completionSummary(uploads: UploadItem[]): string {
  const successCount = succeeded(uploads).length;
  const failCount = uploads.filter((upload) => upload.error).length;

  if (failCount === 0 && allProcessed(uploads)) {
    return readyPrompt(successCount);
  }
  if (failCount === 0) {
    return successCount === 1
      ? "1 file uploaded successfully."
      : `${successCount} files uploaded successfully.`;
  }
  if (successCount === 0) {
    return failureSummary(uploads, failCount);
  }
  return `${successCount} file${successCount !== 1 ? "s" : ""} uploaded, ${failCount} failed.`;
}

/** Close the table and post the summary (or the "ready to proceed" prompt). */
function pushCompletionSummary(state: ChatSliceState, uploads: UploadItem[]): void {
  const failCount = uploads.filter((upload) => upload.error).length;
  const ready = allProcessed(uploads) && failCount === 0;

  state.messages.push({
    sender: "chatbot",
    text: completionSummary(uploads),
    type: ready ? "upload-ready-prompt" : "upload-summary",
  });
}

/**
 * A status update arriving after the table closed: post the prompt if the last
 * file has only now finished processing and we haven't prompted yet.
 */
function pushLateReadyPrompt(state: ChatSliceState, uploads: UploadItem[]): void {
  const hasReadyPrompt = state.messages.some(
    (msg: ChatMessage) => msg.type === "upload-ready-prompt"
  );
  if (!allProcessed(uploads) || hasReadyPrompt) return;

  state.messages.push({
    sender: "chatbot",
    text: readyPrompt(succeeded(uploads).length),
    type: "upload-ready-prompt",
  });
}

/**
 * Apply one upload status update to the chat transcript's upload table,
 * creating the table on first use and closing it once every file has settled.
 */
export function applyUploadUpdate(state: ChatSliceState, payload: UploadUpdatePayload): void {
  if (!payload?.uploadId) return;

  const table = openUploadTable(state);
  mergeUpload(table, payload);

  const uploads = Object.values(table.uploads as UploadMap);
  const allDone = uploads.every((upload) => upload.completed || upload.error);

  if (allDone && uploads.length > 0 && !table.allCompleted) {
    table.allCompleted = true;
    pushCompletionSummary(state, uploads);
  } else if (table.allCompleted) {
    pushLateReadyPrompt(state, uploads);
  }
}
